import cn from 'classnames';
import _ from 'lodash';
import {createElement, HTMLProps, ReactNode} from 'react';
import {getCsMailUrl} from '../../commonSub/function';
import {IS_CLIENT, REGION} from '../constant';
import {Page} from '../constant/page';
import {routeTo} from '../util/PageRouter';
import {getUserAgent} from '../util/userAgent';

export interface IProps extends Omit<HTMLProps<HTMLElement>, 'as'> {
  children: ReactNode;
  as?: 'div' | 'span' | 'p' | 'label' | 'dt' | 'dd';
  height?: string; // 부모에 height이 상대값이라면(ex. 100%), 정확한 px 값을 넣어줘야 한다.
  useBaseline?: boolean;
  width?: string;
  onClickLink?: () => void;
}
/**
 * 높이가 고정적이지 않다면, 마지막 줄의 baseline을 특정할 수 없다.
 * 그 이유는 디바이스에 따라 너비가 달라져 Text의 줄 수가 달라지기 때문이다.
 * (android도 첫줄의 baseline만 사용 가능한다)
 *
 * 그래서 케이스에 맞게 baseline을 이용할지 말지를 결정해야 한다.
 * 판단하기 애매하다면, 디자이너와 논의하도록 하자.
 *
 * 아래는 case별 useBaseline 사용 여부에 관한 내용이다.
 * 참고하도록 하자. (필요한 내용이 있다면 추가하자)
 *
 * useBaseline = true를 사용하는 case
 *   - 특정 높이를 가진 element 내부에 text 요소를 배치하는 경우 (ex. 작품홈)
 *
 * useBaseline = false를 사용하는 case
 *   - 정보 전달을 목적으로 길이에 구애받지 않는 경우 (ex. 공지사항, 작품정보)
 *   - line 수가 유동적인 경우
 *   - 버튼 내부에 위치한 텍스트
 */
export default function Text({
  className,
  children,
  as = 'p',
  height = '100%',
  useBaseline = false,
  onClickLink,
  ...rest
}: IProps) {
  // TODO: children이 배열인 경우에 루프를 돌면서 모든 문자열에 다 적용해주면 좋겠다
  const element =
    typeof children === 'string'
      ? makeStyledText(children, onClickLink)
      : children;

  const textComponent = createElement(
    as,
    {
      className: cn(
        'whitespace-pre-wrap break-all break-words support-break-word',
        className
      ),
      ...rest,
    },
    element
  );
  if (useBaseline) {
    return (
      <div
        className={cn({
          'items-baseline flex before:h-full': useBaseline,
        })}
        style={useBaseline ? {height} : undefined}
      >
        {textComponent}
      </div>
    );
  } else {
    return textComponent;
  }
}

const REGEX = /<\s*([a-zA-Z\-_]+)\s*>(.*?)<\s*\/\s*([a-zA-Z\-_]+)\s*>/;
export function makeStyledText(
  text: string,
  onClickLink?: () => void
): string | ReactNode[] {
  const result = [];
  let remainText = text;
  while (remainText.length > 0) {
    const matched = remainText.match(REGEX);
    if (!matched) {
      break;
    }
    const startIndex = matched.index || 0;
    const prevText = remainText.substr(0, startIndex);
    if (prevText) {
      result.push(prevText);
    }
    const fullText = matched[0];
    const tagMap = getTagMap(matched[1].split('-'));
    const content = matched[2];
    const linkTag = getLinkTag(tagMap);
    if (linkTag) {
      result.push(
        <span
          // <link_xxx>는 underline만 들어간다. (디자인 협의 완료 cc. 안나)
          className="underline cursor-pointer"
          key={result.length}
          onClick={() => {
            onClickLink?.();
            linkTag.onClick();
          }}
        >
          {content}
        </span>
      );
    } else {
      const color = getColor(tagMap);
      const fontWeight = tagMap.bold ? 'bold' : undefined;
      result.push(
        <span
          key={result.length}
          className={color}
          style={
            fontWeight
              ? {
                  fontWeight,
                }
              : undefined
          }
        >
          {content}
        </span>
      );
    }
    remainText = remainText.substr(startIndex + fullText.length);
  }
  if (remainText) {
    if (result.length === 0) {
      return text;
    } else {
      result.push(remainText);
    }
  }
  return result;
}

function getTagMap(tags: string[]) {
  return tags.reduce<{[key: string]: boolean}>(
    (acc, tag) => ((acc[tag] = true), acc),
    {}
  );
}

function getLinkTag(tagMap: ReturnType<typeof getTagMap>) {
  const keys = Object.keys(TAG_LINK_MAP) as Array<keyof typeof TAG_LINK_MAP>;
  for (const key of keys) {
    if (tagMap[key]) {
      return TAG_LINK_MAP[key];
    }
  }
  return;
}

function getColor(tagMap: ReturnType<typeof getTagMap>) {
  const keys = Object.keys(TEXT_COLOR_MAP) as Array<
    keyof typeof TEXT_COLOR_MAP
  >;
  for (const key of keys) {
    if (tagMap[key]) {
      return TEXT_COLOR_MAP[key];
    }
  }
}

export const TEXT_COLOR_MAP = {
  red: 'text-red',
  blue: 'text-blue',
  black: 'text-black',
  white: 'text-white', // 검색 페이지에서 사용
  gold: 'text-gold',
  purple: 'text-purple',
  grey: 'text-grey-08',
};

enum TagLink {
  LinkCs = 'link_cs',
  LinkPrivacy = 'link_privacy',
  LinkDaumCash = 'link_daum_cash',
}
const TAG_LINK_MAP: {
  [key in TagLink]: {
    onClick: () => void;
  };
} = {
  [TagLink.LinkCs]: {
    onClick: () => {
      if (REGION === 'kor') {
        routeTo({page: Page.MoreInquiry});
      } else {
        const userAgent = IS_CLIENT
          ? getUserAgent(window.navigator.userAgent)
          : undefined;
        location.assign(getCsMailUrl({userAgent}));
      }
    },
  },
  [TagLink.LinkPrivacy]: {
    onClick: () => {
      routeTo({page: Page.MorePolicyPersonalInformation});
    },
  },
  [TagLink.LinkDaumCash]: {
    onClick: () => {
      routeTo({page: Page.MoreDaumCashHistory});
    },
  },
};
